import {
  createShopApiGraphqlClient,
  type ShopApiGraphqlClient,
} from "../../../../packages/pvc-product/shop-api-transport";

export const WORK_GALLERY_PATH = "/gallery";

export type PublicWorkGalleryCategory = {
  id: string;
  code: string;
  name: string;
  description: string | null;
  displayOrder: number;
};

export type PublicWorkGalleryAsset = {
  id: string;
  name: string;
  preview: string;
  source: string;
  width: number;
  height: number;
};

export type PublicWorkGalleryItem = {
  id: string;
  title: string;
  description: string | null;
  categoryId: string;
  categoryCode: string;
  categoryName: string;
  coverAsset: PublicWorkGalleryAsset;
  assets: PublicWorkGalleryAsset[];
  altText: string | null;
  featuredOnHomepage: boolean;
  displayOrder: number;
};

export type PublicWorkGallery = {
  categories: PublicWorkGalleryCategory[];
  items: PublicWorkGalleryItem[];
};

const PUBLIC_WORK_GALLERY_QUERY = `
  query HappyPrintWorkGallery {
    happyPrintWorkGallery {
      categories {
        id
        code
        name
        description
        displayOrder
      }
      items {
        id
        title
        description
        categoryId
        categoryCode
        categoryName
        coverAsset {
          id
          name
          preview
          source
          width
          height
        }
        assets {
          id
          name
          preview
          source
          width
          height
        }
        altText
        featuredOnHomepage
        displayOrder
      }
    }
  }
`;

function record(value: unknown, path: string): Record<string, unknown> {
  if (value == null || typeof value !== "object" || Array.isArray(value)) {
    throw new Error(`${path} must be an object`);
  }
  return value as Record<string, unknown>;
}

function requiredString(value: unknown, path: string): string {
  if (typeof value !== "string" || !value.trim()) {
    throw new Error(`${path} must be a non-empty string`);
  }
  return value;
}

function nullableString(value: unknown, path: string): string | null {
  if (value == null) return null;
  if (typeof value !== "string") throw new Error(`${path} must be text or null`);
  const normalized = value.trim();
  return normalized || null;
}

function requiredBoolean(value: unknown, path: string): boolean {
  if (typeof value !== "boolean") throw new Error(`${path} must be a boolean`);
  return value;
}

function requiredInteger(value: unknown, path: string): number {
  if (typeof value !== "number" || !Number.isSafeInteger(value)) {
    throw new Error(`${path} must be an integer`);
  }
  return value;
}

function nonNegativeInteger(value: unknown, path: string): number {
  const number = requiredInteger(value, path);
  if (number < 0) throw new Error(`${path} must be zero or greater`);
  return number;
}

function decodeAsset(value: unknown, path: string): PublicWorkGalleryAsset {
  const asset = record(value, path);
  return {
    id: requiredString(asset.id, `${path}.id`),
    name: requiredString(asset.name, `${path}.name`),
    preview: requiredString(asset.preview, `${path}.preview`),
    source: requiredString(asset.source, `${path}.source`),
    width: nonNegativeInteger(asset.width, `${path}.width`),
    height: nonNegativeInteger(asset.height, `${path}.height`),
  };
}

export function decodePublicWorkGallery(data: unknown): PublicWorkGallery {
  const root = record(data, "data");
  const gallery = record(root.happyPrintWorkGallery, "data.happyPrintWorkGallery");

  if (!Array.isArray(gallery.categories)) {
    throw new Error("data.happyPrintWorkGallery.categories must be an array");
  }
  if (!Array.isArray(gallery.items)) {
    throw new Error("data.happyPrintWorkGallery.items must be an array");
  }

  const categories = gallery.categories.map((raw, index) => {
    const path = `data.happyPrintWorkGallery.categories[${index}]`;
    const category = record(raw, path);
    return {
      id: requiredString(category.id, `${path}.id`),
      code: requiredString(category.code, `${path}.code`),
      name: requiredString(category.name, `${path}.name`),
      description: nullableString(category.description, `${path}.description`),
      displayOrder: nonNegativeInteger(category.displayOrder, `${path}.displayOrder`),
    };
  });

  const categoryIds = new Set(categories.map((category) => category.id));
  const items = gallery.items.map((raw, index) => {
    const path = `data.happyPrintWorkGallery.items[${index}]`;
    const item = record(raw, path);
    const categoryId = requiredString(item.categoryId, `${path}.categoryId`);
    if (!categoryIds.has(categoryId)) {
      throw new Error(`${path}.categoryId references an unavailable category`);
    }
    if (!Array.isArray(item.assets)) {
      throw new Error(`${path}.assets must be an array`);
    }
    return {
      id: requiredString(item.id, `${path}.id`),
      title: requiredString(item.title, `${path}.title`),
      description: nullableString(item.description, `${path}.description`),
      categoryId,
      categoryCode: requiredString(item.categoryCode, `${path}.categoryCode`),
      categoryName: requiredString(item.categoryName, `${path}.categoryName`),
      coverAsset: decodeAsset(item.coverAsset, `${path}.coverAsset`),
      assets: item.assets.map((asset, assetIndex) =>
        decodeAsset(asset, `${path}.assets[${assetIndex}]`),
      ),
      altText: nullableString(item.altText, `${path}.altText`),
      featuredOnHomepage: requiredBoolean(
        item.featuredOnHomepage,
        `${path}.featuredOnHomepage`,
      ),
      displayOrder: nonNegativeInteger(item.displayOrder, `${path}.displayOrder`),
    };
  });

  return { categories, items };
}

export function resolveWorkGalleryAssetUrl(
  value: string,
  shopApiEndpoint: string = import.meta.env.VITE_SHOP_API_URL || "/shop-api",
): string {
  if (/^(?:https?:|data:|blob:)/i.test(value)) return value;
  if (typeof window === "undefined") return value;
  const endpoint = new URL(shopApiEndpoint, window.location.origin);
  return new URL(value, endpoint.origin).toString();
}

export function createPublicWorkGalleryApi(client: ShopApiGraphqlClient) {
  return {
    async load(signal?: AbortSignal): Promise<PublicWorkGallery> {
      const data = await client.execute({
        operationName: "HappyPrintWorkGallery",
        document: PUBLIC_WORK_GALLERY_QUERY,
        signal,
      });
      return decodePublicWorkGallery(data);
    },
  };
}

export function createBrowserPublicWorkGalleryApi() {
  return createPublicWorkGalleryApi(
    createShopApiGraphqlClient({
      endpoint: import.meta.env.VITE_SHOP_API_URL || "/shop-api",
    }),
  );
}
